import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from "react-redux"
import Select from 'react-select'

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import useFilterSearchPagination from 'pages/parts/useFilterSearchPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'

import { httpOk, GET, DELETE } from 'helpers/api'
import { formatDate, returnSign } from "helpers/helpers"

// Журнал событий: кто и когда делал чувствительные действия.
//
// Три вкладки, потому что это три разных вопроса, и смешивать их в одну ленту нельзя:
//   «Опасные» — то, ради чего сюда заходят: удаления чеков, правки остатков, выдача прав.
//               Открывается первой, под неё на бэке частичный индекс по severity = 2.
//   «Все»     — общая лента с фильтрами, когда разбираются в конкретном случае.
//   «Входы»   — отдельная таблица user_sessions: у входа есть начало и конец, это состояние,
//               а не событие, и в общей ленте оно бы всё утопило (~3-5 тысяч в сутки).
//
// Раскладка как в iiko: фильтр слева отдельной панелью и всегда на виду, справа — только
// таблица. Здесь фильтром пользуются в каждом заходе и подбирают его с нескольких попыток
// («не тот период», «не тот сотрудник»), а выпадашка после каждого показа закрывается
// и прячет то, что уже выбрано.
//
// Что именно изменилось, показываем только там, где бэк это записал (цена, права, статус).
// По остальному — как в iiko — фиксируется сам факт: «техкарта изменена, тогда-то, тем-то».

const TABS = {
	DANGEROUS: 'dangerous',
	ALL: 'all',
	SESSIONS: 'sessions',
}

// Источник действия. Ключ переводов один на всё приложение, поэтому префиксуем.
const SOURCES = ['web', 'cashier', 'mobile', 'bot', 'website', 'api', 'cron', 'terminal', 'system', 'migration']

// Категории справочника audit_actions (см. миграцию create_audit_logs_table).
const CATEGORIES = ['cheque', 'document', 'catalog', 'employee', 'finance', 'client', 'auth', 'settings', 'system']

const SEVERITIES = [2, 1, 0]

function Index() {
	const { t } = useTranslation()
	const date = new Date()

	const reduxAccount = useSelector(state => state.account)

	const [tab, setTab] = useState(TABS.DANGEROUS)
	const [data, setData] = useState({ 'data': [], 'total': 0 })
	const [actions, setActions] = useState([])
	const [poses, setPoses] = useState([])
	const [employees, setEmployees] = useState([])
	const [expanded, setExpanded] = useState({})

	const initialFilterData = {
		start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
		end_date: formatDate(new Date(), 'yyyy-MM-dd'),
		pos_id: reduxAccount.pos_id,
		category: '',
		action: '',
		severity: '',
		created_by: '',
		source: '',
		search: '',
	}
	const initialPagination = { url: '', response: null, size: 50 }
	const {
		filterData,
		pagination,
		setPagination,
		patchFilterData,
		applySearch,
		applyFilters,
		paginate,
		resetFilters,
	} = useFilterSearchPagination({ initialFilterData, initialPagination })

	function baseUrl(currentTab = tab) {
		if (currentTab === TABS.SESSIONS) return '/sessions'
		if (currentTab === TABS.DANGEROUS) return '/audit-logs/dangerous'

		return '/audit-logs'
	}

	async function searchWithFilter(customFilterData = filterData, currentTab = tab) {
		var urlParams = ""
		var filterDataCopy = { ...customFilterData }

		if (filterDataCopy.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterDataCopy.pos_id
		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterDataCopy.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
		if (filterDataCopy.created_by)
			urlParams += returnSign(urlParams) + 'created_by=' + filterDataCopy.created_by
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		// Действие, источник и поиск по объекту у входов не спрашиваются — там другие поля.
		if (currentTab !== TABS.SESSIONS) {
			if (filterDataCopy.category)
				urlParams += returnSign(urlParams) + 'category=' + filterDataCopy.category
			if (filterDataCopy.action)
				urlParams += returnSign(urlParams) + 'action=' + filterDataCopy.action
			if (filterDataCopy.source)
				urlParams += returnSign(urlParams) + 'source=' + filterDataCopy.source
			if (filterDataCopy.search)
				urlParams += returnSign(urlParams) + 'search=' + filterDataCopy.search
			// На вкладке опасных важность задаёт сам маршрут, спрашивать её там нечего.
			if (currentTab === TABS.ALL && filterDataCopy.severity !== '' && filterDataCopy.severity !== null)
				urlParams += returnSign(urlParams) + 'severity=' + filterDataCopy.severity
		}

		urlParams += returnSign(urlParams) + 'limit=' + (pagination.size ?? 50)

		const filterUrl = baseUrl(currentTab) + urlParams
		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data })
		}
	}

	function changeTab(nextTab) {
		if (nextTab === tab) return

		setTab(nextTab)
		setExpanded({})
		const nextFilterData = { ...filterData, 'page': 0 }
		paginate(0)
		searchWithFilter(nextFilterData, nextTab)
	}

	function resetFilter() {
		resetFilters({
			...initialFilterData,
			pos_id: filterData.pos_id ?? '',
		}, searchWithFilter)
	}

	function handlePaginate(page) {
		const nextFilterData = { ...filterData, page }
		paginate(page)
		searchWithFilter(nextFilterData)
	}

	// Готовые периоды: девять заходов из десяти — это «сегодня» или «вчера»,
	// и ради них не должно быть двух открытий календаря.
	function setPeriod(from, to = new Date()) {
		patchFilterData({
			'start_date': formatDate(from, 'yyyy-MM-dd'),
			'end_date': formatDate(to, 'yyyy-MM-dd'),
		})
	}

	function periodIsActive(from, to = new Date()) {
		return filterData.start_date === formatDate(from, 'yyyy-MM-dd')
			&& filterData.end_date === formatDate(to, 'yyyy-MM-dd')
	}

	function toggleExpanded(id) {
		setExpanded({ ...expanded, [id]: !expanded[id] })
	}

	// Опасное — красным, важное — жёлтым: владелец должен видеть строку, не читая её.
	function severityClass(severity) {
		if (severity === 2) return 'bg-danger text-white'
		if (severity === 1) return 'bg-warning'

		return 'bg-light text-dark border'
	}

	function severityName(severity) {
		const translated = t('audit_severity_' + severity)

		return translated !== 'audit_severity_' + severity ? translated : String(severity)
	}

	function sourceName(source) {
		const translated = t('audit_source_' + source)

		return translated !== 'audit_source_' + source ? translated : source
	}

	// Выход: когда и почему закрыт сеанс. Активный — с последней активностью устройства;
	// у входов кабинета до 18.10.2026 нет device_id, их выход не записывался — статус неизвестен
	function sessionStatus(item) {
		if (item.ended_at) {
			const reasonKey = 'audit_session_end_' + item.end_reason
			const reason = item.end_reason && t(reasonKey) !== reasonKey ? t(reasonKey) : null
			return (
				<>
					<div>{formatDate(item.ended_at, 'dd.MM.yyyy HH:mm')}</div>
					{reason && <div className="text-muted">{reason}</div>}
				</>
			)
		}

		if (!item.device_id && (item.app_code ?? 'web') === 'web') {
			return <span className="badge bg-secondary" title={t('audit_session_unknown_hint')}>{t('audit_session_unknown')}</span>
		}

		return (
			<>
				<span className="badge bg-success">{t('audit_session_active')}</span>
				{item.last_seen_at && item.last_seen_at !== item.started_at &&
					<div className="text-muted">{t('audit_session_last_seen')}: {formatDate(item.last_seen_at, 'dd.MM HH:mm')}</div>
				}
				{item.can_revoke &&
					<div>
						<button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => revokeSession(item)}>
							{t('audit_session_terminate')}
						</button>
					</div>
				}
			</>
		)
	}

	// Завершить сеанс сотрудника: удаляется токен этого входа, устройство выкидывает
	// при следующем запросе. Только для входов на своём токене (can_revoke)
	async function revokeSession(item) {
		const response = await DELETE(`/sessions/${item.id}`, {}, { loader: true })
		if (httpOk(response)) searchWithFilter()
	}

	function categoryName(category) {
		const translated = t('audit_category_' + category)

		return translated !== 'audit_category_' + category ? translated : category
	}

	function actionName(item) {
		if (item.name_key) {
			const translated = t(item.name_key)
			if (translated !== item.name_key) return translated
		}

		// Действие, для которого перевода ещё нет, показываем кодом, а не пустотой:
		// журнал должен читаться даже когда в справочник добавили строку без ключа.
		return item.action ?? item.code
	}

	// Список действий сужаем по уже выбранной категории — иначе в нём полсотни строк
	// вперемешку, и нужную приходится искать глазами.
	function actionOptions() {
		var options = actions
		if (tab === TABS.DANGEROUS) options = options.filter(item => item.severity === 2)
		if (filterData.category) options = options.filter(item => item.category === filterData.category)

		return options
	}

	// «было → стало» разворачивается по клику: в таблице такому месту нет,
	// а нужно оно в одном случае из десяти.
	function renderChanges(item) {
		if (!item.changes) return null

		var changes = item.changes
		if (typeof changes === 'string') {
			try {
				changes = JSON.parse(changes)
			} catch (error) {
				return null
			}
		}

		const keys = Object.keys(changes ?? {})
		if (keys.length === 0) return null

		return (
			<table className="table table-sm mb-0">
				<tbody>
					{keys.map((key) => (
						<tr key={key}>
							<td className="w-25"><b>{t(key) !== key ? t(key) : key}</b></td>
							<td className="text-muted">{formatValue(changes[key]?.[0])}</td>
							<td>→ {formatValue(changes[key]?.[1])}</td>
						</tr>
					))}
				</tbody>
			</table>
		)
	}

	function formatValue(value) {
		if (value === null || value === undefined || value === '') return '—'
		if (Array.isArray(value)) return value.join(', ')
		if (typeof value === 'object') return JSON.stringify(value)

		return String(value)
	}

	async function getActions() {
		const response = await GET('/audit-logs/actions')
		if (httpOk(response)) setActions(response.data?.data ?? [])
	}

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	async function getEmployees() {
		const response = await GET('/helper/employees')
		if (httpOk(response)) setEmployees(response.data)
	}

	useEffect(() => {
		getActions()
		getPoses()
		getEmployees()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('audit_logs')}
				</h4>
			</div>

			<div className="event-log">
				{/* ФИЛЬТР */}
				<div className="event-log-filter">
					<div className="card mb-0">
						<div className="card-body">
							<div className="event-log-filter-title">{t('audit_filter_events')}</div>

							<div className="event-log-group">
								<div className="event-log-group-title">{t('period')}</div>

								<div className="event-log-periods">
									<button
										className={`event-log-period ${periodIsActive(new Date()) ? 'active' : ''}`}
										onClick={() => setPeriod(new Date())}>
										{t('today')}
									</button>
									<button
										className={`event-log-period ${periodIsActive(
											new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1),
											new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1),
										) ? 'active' : ''}`}
										onClick={() => setPeriod(
											new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1),
											new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1),
										)}>
										{t('yesterday')}
									</button>
									<button
										className={`event-log-period ${periodIsActive(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 6)) ? 'active' : ''}`}
										onClick={() => setPeriod(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 6))}>
										{t('last_7_days')}
									</button>
									<button
										className={`event-log-period ${periodIsActive(new Date(date.getFullYear(), date.getMonth(), 1)) ? 'active' : ''}`}
										onClick={() => setPeriod(new Date(date.getFullYear(), date.getMonth(), 1))}>
										{t('current_month')}
									</button>
								</div>

								<div className="event-log-dates">
									<div className="form-group mb-0">
										<PeriodPicker
											startDate={filterData.start_date}
											endDate={filterData.end_date}
											onChange={(period) => patchFilterData(period)}
											withTime={false}
											presets={[]}
											minDate={new Date(new Date().getFullYear() - 2, 0, 1)}
											maxDate={new Date()}
										/>
									</div>
								</div>
							</div>

							{tab !== TABS.SESSIONS &&
								<div className="event-log-group">
									{tab === TABS.ALL &&
										<div className="form-group">
											<label>{t('audit_importance')}</label>
											<Select
												isClearable
												options={SEVERITIES.map(item => ({ 'id': item }))}
												value={filterData.severity !== '' ? { 'id': filterData.severity } : ''}
												onChange={(option) => patchFilterData({ 'severity': option ? option.id : '' })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => severityName(option.id)}
												getOptionValue={(option) => option.id}
											/>
										</div>
									}

									<div className="form-group">
										<label>{t('category')}</label>
										<Select
											isClearable
											options={CATEGORIES.map(item => ({ 'code': item }))}
											value={filterData.category ? { 'code': filterData.category } : ''}
											onChange={(option) => patchFilterData({ 'category': option ? option.code : '', 'action': '' })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => categoryName(option.code)}
											getOptionValue={(option) => option.code}
										/>
									</div>

									<div className="form-group mb-0">
										<label>{t('action')}</label>
										<Select
											isClearable
											options={actionOptions()}
											value={actions.find(option => option.code === filterData.action) || ''}
											onChange={(option) => patchFilterData({ 'action': option ? option.code : '' })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => actionName(option)}
											getOptionValue={(option) => option.code}
										/>
									</div>
								</div>
							}

							<div className="event-log-group">
								<div className="form-group">
									<label>{t('pos')}</label>
									<Select
										isClearable
										options={poses}
										value={poses.find(option => option.id === filterData.pos_id) || ''}
										onChange={(option) => patchFilterData({ 'pos_id': option ? option.id : null })}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>

								<div className={`form-group ${tab === TABS.SESSIONS ? 'mb-0' : ''}`}>
									<label>{t('employee')}</label>
									<Select
										isClearable
										options={employees}
										value={employees.find(option => option.login === filterData.created_by) || ''}
										onChange={(option) => patchFilterData({ 'created_by': option ? option.login : '' })}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name ?? option.login}
										getOptionValue={(option) => option.login}
									/>
								</div>

								{tab !== TABS.SESSIONS &&
									<div className="form-group mb-0">
										<label>{t('source')}</label>
										<Select
											isClearable
											options={SOURCES.map(item => ({ 'code': item }))}
											value={filterData.source ? { 'code': filterData.source } : ''}
											onChange={(option) => patchFilterData({ 'source': option ? option.code : '' })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => sourceName(option.code)}
											getOptionValue={(option) => option.code}
										/>
									</div>
								}
							</div>

							<div className="event-log-actions">
								<button className="btn btn-primary"
									onClick={() => applyFilters(searchWithFilter)}>
									{t('audit_show_events')}
								</button>
								<button className="btn btn-outline-dark" onClick={resetFilter}>
									{t('reset')}
								</button>
							</div>
						</div>
					</div>
				</div>

				{/* СОБЫТИЯ */}
				<div className="event-log-content">
					<div className="card mb-0">
						<div className="card-body">
							<div className="event-log-toolbar">
								<ul className="nav nav-tabs">
									<li className="nav-item">
										<button className={`nav-link ${tab === TABS.DANGEROUS ? 'active' : ''}`}
											onClick={() => changeTab(TABS.DANGEROUS)}>
											{t('audit_dangerous')}
										</button>
									</li>
									<li className="nav-item">
										<button className={`nav-link ${tab === TABS.ALL ? 'active' : ''}`}
											onClick={() => changeTab(TABS.ALL)}>
											{t('audit_all_actions')}
										</button>
									</li>
									<li className="nav-item">
										<button className={`nav-link ${tab === TABS.SESSIONS ? 'active' : ''}`}
											onClick={() => changeTab(TABS.SESSIONS)}>
											{t('audit_sessions')}
										</button>
									</li>
								</ul>

								{tab !== TABS.SESSIONS &&
									<div className="event-log-search">
										<FilterSearch
											value={filterData.search}
											onSearchChange={(search) => applySearch(search, searchWithFilter)}>
										</FilterSearch>
									</div>
								}
							</div>

							{tab === TABS.SESSIONS ?
								<div className="table-responsive table-sticky-page">
									<table className="table table-bordered mb-0">
										<thead>
											<tr>
												<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
												<th style={{ 'width': '20%' }}>{t('employee')}</th>
												<th style={{ 'width': '15%' }}>{t('pos')}</th>
												<th style={{ 'width': '20%' }}>{t('device')}</th>
												<th style={{ 'width': '10%' }}>IP</th>
												<th style={{ 'width': '15%' }} className="text-center">{t('audit_session_started')}</th>
												<th style={{ 'width': '15%' }} className="text-center">{t('audit_session_ended')}</th>
											</tr>
										</thead>
										<tbody>
											{(data.data ?? []).map((item, index) => (
												<tr key={item.id}>
													<td className="text-center">{index + 1}</td>
													<td>
														<div>{item.actor_name || item.first_name || item.created_by}</div>
														<div className="text-muted">{item.created_by}</div>
													</td>
													<td>{item.pos_name}</td>
													<td>
														<div>{sourceName(item.app_code ?? 'web')}</div>
														<div className="text-muted">
															{/* Приложения до 19.10.2026 модель не слали — тогда хотя бы платформа */}
															{[item.device_model || { android: 'Android', ios: 'iPhone' }[item.platform], item.app_version].filter(Boolean).join(' · ') || item.app_code}
														</div>
													</td>
													<td>
														<div>{item.ip}</div>
														{item.location && <div className="text-muted">{item.location}</div>}
													</td>
													<td className="text-center">{formatDate(item.started_at, 'dd.MM.yyyy HH:mm')}</td>
													<td className="text-center">
														{sessionStatus(item)}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
								:
								<div className="table-responsive table-sticky-page">
									<table className="table table-bordered mb-0">
										<thead>
											<tr>
												<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
												<th style={{ 'width': '13%' }} className="text-center">{t('date_and_time')}</th>
												<th style={{ 'width': '20%' }}>{t('action')}</th>
												<th style={{ 'width': '17%' }}>{t('employee')}</th>
												<th style={{ 'width': '25%' }}>{t('object')}</th>
												<th style={{ 'width': '15%' }}>{t('reason')}</th>
												<th style={{ 'width': '10%' }} className="text-center">{t('source')}</th>
											</tr>
										</thead>
										<tbody>
											{(data.data ?? []).map((item, index) => (
												<React.Fragment key={item.id}>
													<tr className={item.changes ? 'cursor-pointer' : ''}
														onClick={() => item.changes && toggleExpanded(item.id)}>
														<td className="text-center">{index + 1}</td>
														<td className="text-center">
															{formatDate(item.occurred_at, 'dd.MM.yyyy HH:mm')}
														</td>
														<td>
															<span className={`badge ${severityClass(item.severity)}`}>
																{actionName(item)}
															</span>
														</td>
														<td>
															<div>{item.actor_name || item.created_by}</div>
															{item.actor_name && <div className="text-muted">{item.created_by}</div>}
														</td>
														<td>
															{item.entity_label}
															{item.changes &&
																<i className={`uil uil-angle-${expanded[item.id] ? 'up' : 'down'} ms-1`}></i>
															}
														</td>
														<td>{item.reason}</td>
														<td className="text-center text-muted">
															{sourceName(item.source)}
														</td>
													</tr>
													{expanded[item.id] &&
														<tr>
															<td colSpan="7" className="bg-light">
																{renderChanges(item)}
															</td>
														</tr>
													}
												</React.Fragment>
											))}
										</tbody>
									</table>
								</div>
							}

							{(data.data ?? []).length === 0 &&
								<div className="text-center text-muted p-4">{t('list_empty')}</div>
							}

							<div className="event-log-footer">
								<div className="event-log-total">
									{t('quantity')}: <b>{data.total ?? 0}</b>
								</div>
								<FilterPagination
									pagination={pagination}
									paginate={handlePaginate}>
								</FilterPagination>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export default Index
